<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import { useLeadsStore, LEAD_STAGES, LEAD_SOURCES } from 'dashboard/stores/leads';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const leadsStore = useLeadsStore();

const leadId = computed(() => Number(route.params.leadId));
const lead = computed(() => leadsStore.getRecord(leadId.value));
const isSaving = computed(() => leadsStore.getUIFlags.updatingItem);
const isFetchingReminders = computed(
  () => leadsStore.getUIFlags.fetchingReminders
);
const reminders = computed(() =>
  [...leadsStore.leadReminders].sort(
    (a, b) => new Date(a.remindAt) - new Date(b.remindAt)
  )
);

const agents = computed(() => store.getters['agents/getAgents']);

const form = reactive({
  stage: '',
  source: '',
  dealValue: '',
  expectedCloseDate: '',
  assignedAgentId: '',
});

const syncFormFromLead = () => {
  if (!lead.value?.id) return;
  form.stage = lead.value.stage || '';
  form.source = lead.value.source || '';
  form.dealValue = lead.value.dealValue || '';
  form.expectedCloseDate = lead.value.expectedCloseDate || '';
  form.assignedAgentId = lead.value.assignedAgent?.id || '';
};

watch(lead, syncFormFromLead, { immediate: true });

onMounted(async () => {
  store.dispatch('agents/get');
  await leadsStore.show(leadId.value);
  await leadsStore.getReminders(leadId.value);
  syncFormFromLead();
});

const save = async () => {
  try {
    await leadsStore.update({
      id: leadId.value,
      stage: form.stage,
      source: form.source || null,
      dealValue: form.dealValue || null,
      expectedCloseDate: form.expectedCloseDate || null,
      assignedAgentId: form.assignedAgentId || null,
    });
    useAlert(t('LEADS.DETAIL.SAVED'));
  } catch (error) {
    useAlert(t('LEADS.DETAIL.ERROR'));
  }
};

const goBack = () => {
  router.push({ name: 'leads_dashboard_index' });
};

const newReminderNote = ref('');
const newReminderAt = ref('');

const addReminder = async () => {
  if (!newReminderAt.value) return;
  try {
    await leadsStore.createReminder(leadId.value, {
      remindAt: newReminderAt.value,
      note: newReminderNote.value,
    });
    newReminderNote.value = '';
    newReminderAt.value = '';
  } catch (error) {
    useAlert(t('LEADS.REMINDERS.CREATED_ERROR'));
  }
};

const toggleReminderDone = reminder => {
  leadsStore.updateReminder(leadId.value, reminder.id, {
    completed: !reminder.completed,
  });
};

const deleteReminder = async reminder => {
  try {
    await leadsStore.deleteReminder(leadId.value, reminder.id);
  } catch (error) {
    useAlert(t('LEADS.REMINDERS.DELETE_ERROR'));
  }
};
</script>

<template>
  <div class="h-full overflow-y-auto">
    <div class="max-w-2xl mx-auto p-6">
      <button
        class="text-sm text-n-slate-11 hover:text-n-slate-12 mb-4"
        @click="goBack"
      >
        &larr; {{ t('LEADS.DETAIL.BACK') }}
      </button>

      <div v-if="lead?.id" class="flex flex-col gap-6">
        <div class="flex items-center gap-3">
          <Avatar
            :src="lead.contact?.thumbnail"
            :size="40"
            :name="lead.contact?.name"
          />
          <div>
            <h1 class="text-lg font-medium text-n-slate-12">
              {{ lead.contact?.name || lead.contact?.email }}
            </h1>
            <p v-if="lead.channel" class="text-xs text-n-slate-10">
              {{ t('LEADS.CHANNEL.LABEL') }}: {{ lead.channel }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-xs font-medium text-n-slate-11">{{
              t('LEADS.STAGE.LABEL')
            }}</label>
            <select
              v-model="form.stage"
              class="mt-1 w-full rounded-md border border-n-weak bg-n-solid-1 p-2 text-sm"
            >
              <option v-for="stage in LEAD_STAGES" :key="stage" :value="stage">
                {{ t(`LEADS.STAGES.${stage}`) }}
              </option>
            </select>
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-11">{{
              t('LEADS.SOURCE.LABEL')
            }}</label>
            <select
              v-model="form.source"
              class="mt-1 w-full rounded-md border border-n-weak bg-n-solid-1 p-2 text-sm"
            >
              <option value="">{{ t('LEADS.SOURCE.UNSET') }}</option>
              <option v-for="source in LEAD_SOURCES" :key="source" :value="source">
                {{ t(`LEADS.SOURCE.OPTIONS.${source}`) }}
              </option>
            </select>
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-11">{{
              t('LEADS.DEAL_VALUE.LABEL')
            }}</label>
            <input
              v-model="form.dealValue"
              type="number"
              min="0"
              class="mt-1 w-full rounded-md border border-n-weak bg-n-solid-1 p-2 text-sm"
            />
          </div>

          <div>
            <label class="text-xs font-medium text-n-slate-11">{{
              t('LEADS.EXPECTED_CLOSE_DATE.LABEL')
            }}</label>
            <input
              v-model="form.expectedCloseDate"
              type="date"
              class="mt-1 w-full rounded-md border border-n-weak bg-n-solid-1 p-2 text-sm"
            />
          </div>

          <div class="col-span-2">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('LEADS.ASSIGNED_AGENT.LABEL')
            }}</label>
            <select
              v-model="form.assignedAgentId"
              class="mt-1 w-full rounded-md border border-n-weak bg-n-solid-1 p-2 text-sm"
            >
              <option value="">
                {{ t('LEADS.ASSIGNED_AGENT.UNASSIGNED') }}
              </option>
              <option
                v-for="agent in agents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </div>
        </div>

        <button
          class="self-start px-4 py-2 text-sm font-medium text-white bg-n-blue-9 rounded-md disabled:opacity-50"
          :disabled="isSaving"
          @click="save"
        >
          {{ t('LEADS.DETAIL.SAVE') }}
        </button>

        <div class="border-t border-n-weak pt-4">
          <h2 class="text-sm font-medium text-n-slate-12 mb-3">
            {{ t('LEADS.REMINDERS.HEADER') }}
          </h2>

          <div class="flex flex-col gap-2 mb-4">
            <textarea
              v-model="newReminderNote"
              :placeholder="t('LEADS.REMINDERS.NOTE_PLACEHOLDER')"
              class="w-full rounded-md border border-n-weak bg-n-solid-1 p-2 text-sm"
              rows="2"
            />
            <div class="flex items-center gap-2">
              <input
                v-model="newReminderAt"
                type="datetime-local"
                class="rounded-md border border-n-weak bg-n-solid-1 p-2 text-sm"
              />
              <button
                class="px-3 py-2 text-sm font-medium text-white bg-n-blue-9 rounded-md disabled:opacity-50"
                :disabled="!newReminderAt"
                @click="addReminder"
              >
                {{ t('LEADS.REMINDERS.ADD') }}
              </button>
            </div>
          </div>

          <div v-if="isFetchingReminders" class="text-xs text-n-slate-10">
            {{ t('LEADS.LOADING') }}
          </div>
          <div v-else-if="!reminders.length" class="text-xs text-n-slate-10">
            {{ t('LEADS.REMINDERS.EMPTY_STATE') }}
          </div>
          <ul v-else class="flex flex-col gap-2">
            <li
              v-for="reminder in reminders"
              :key="reminder.id"
              class="flex items-start justify-between gap-2 p-2 rounded-md border border-n-weak"
              :class="{ 'opacity-50': reminder.completed }"
            >
              <div>
                <p class="text-sm text-n-slate-12">
                  {{ new Date(reminder.remindAt).toLocaleString() }}
                </p>
                <p v-if="reminder.note" class="text-xs text-n-slate-11">
                  {{ reminder.note }}
                </p>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <button
                  class="text-xs text-n-slate-11 hover:text-n-slate-12"
                  @click="toggleReminderDone(reminder)"
                >
                  {{ t('LEADS.REMINDERS.MARK_DONE') }}
                </button>
                <button
                  class="text-xs text-n-ruby-9 hover:text-n-ruby-10"
                  @click="deleteReminder(reminder)"
                >
                  {{ t('LEADS.REMINDERS.DELETE') }}
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
